// filepath: apps/frontend/src/router/index.ts
import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/views/RegisterView.vue'),
      meta: { public: true },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/ForgotPasswordView.vue'),
      meta: { public: true },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/ResetPasswordView.vue'),
      meta: { public: true },
    },
    {
      path: '/external-login',
      name: 'external-login',
      component: () => import('@/views/ExternalLoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/oauth/callback',
      name: 'oauth-callback',
      component: () => import('@/views/OAuthCallbackView.vue'),
      meta: { public: true },
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      component: () => import('@/views/VerifyEmailView.vue'),
      meta: { public: true },
    },
    {
      path: '/external-sign/:token',
      name: 'external-sign',
      component: () => import('@/views/ExternalSignView.vue'),
      meta: { public: true },
    },
    {
      path: '/pricing',
      name: 'pricing',
      component: () => import('@/views/PricingView.vue'),
      meta: { public: true },
    },
    {
      path: '/impressum',
      name: 'impressum',
      component: () => import('@/views/ImpressumView.vue'),
      meta: { public: true },
    },
    {
      path: '/datenschutz',
      name: 'datenschutz',
      component: () => import('@/views/DatenschutzView.vue'),
      meta: { public: true },
    },
    {
      path: '/agb',
      name: 'agb',
      component: () => import('@/views/AgbView.vue'),
      meta: { public: true },
    },
    {
      path: '/accept-invitation/:token',
      name: 'accept-invitation',
      component: () => import('@/views/AcceptInvitationView.vue'),
      meta: { public: true },
    },
    {
      path: '/',
      component: () => import('@/layouts/MainLayout.vue'),
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/views/DashboardView.vue'),
        },
        {
          path: 'documents',
          name: 'documents',
          component: () => import('@/views/DocumentsView.vue'),
        },
        {
          path: 'templates',
          name: 'templates',
          component: () => import('@/views/TemplatesView.vue'),
        },
        {
          path: 'documents/:id/sign',
          name: 'sign-document',
          component: () => import('@/views/SignDocumentView.vue'),
        },
        {
          path: 'security',
          name: 'security',
          component: () => import('@/views/SecurityView.vue'),
        },
        {
          path: 'admin',
          name: 'admin',
          component: () => import('@/views/AdminView.vue'),
          meta: { admin: true },
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('@/views/SettingsView.vue'),
        },
        {
          // Public seal-verification tool. Lives under the app shell so a logged-in
          // user keeps the nav menu; logged-out external visitors still reach it
          // (meta.public), just with the slim shell (sidebar hidden, no user).
          path: 'verify',
          name: 'verify',
          component: () => import('@/views/VerifyView.vue'),
          meta: { public: true },
        },
        {
          path: 'billing',
          name: 'billing',
          component: () => import('@/views/BillingView.vue'),
          meta: { admin: true },
        },
        {
          path: 'team',
          name: 'team',
          component: () => import('@/views/TeamView.vue'),
          meta: { admin: true },
        },
        {
          path: 'help',
          name: 'help',
          component: () => import('@/views/HelpView.vue'),
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],
});

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore();

  // Tokens live in httpOnly cookies, so JS can't inspect them — probe the
  // session once via /me (with a refresh fallback) before guarding routes.
  if (!authStore.initialized) {
    await authStore.initAuth();
  }

  const isPublic = to.meta.public;
  const requiresAuth = !isPublic;
  const requiresAdmin = to.meta.admin;
  
  if (requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } });
  } else if (
    isPublic &&
    authStore.isAuthenticated &&
    to.name !== 'verify-email' &&
    to.name !== 'verify' &&
    !['impressum', 'datenschutz', 'agb'].includes(to.name as string)
  ) {
    // The seal-verification page and the legal pages are public tools/documents,
    // usable while logged in too — don't bounce authenticated users away from them.
    next({ name: 'dashboard' });
  } else if (requiresAdmin && authStore.user?.role !== 'ADMIN') {
    next({ name: 'dashboard' });
  } else {
    next();
  }
});

export default router;
